<template>
  <div class="space-y-5">
    <div class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div class="mb-4">
        <h3 class="text-base font-semibold text-zinc-900">Payments received</h3>
        <p class="mt-1 text-sm text-zinc-500">Review every member payment and open a row for its full details.</p>
      </div>
      <div class="flex flex-wrap items-end gap-3">
      <label class="block">
        <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Search</span>
        <input
          v-model.trim="filters.q"
          type="text"
          class="h-11 w-64 rounded-md border border-zinc-300 px-3 text-sm text-zinc-900 outline-none transition focus:border-[#2D8CFF] focus:ring-1 focus:ring-[#2D8CFF]"
          placeholder="Payment ID, order, intent..."
          @keyup.enter="refreshFromFirstPage"
        >
      </label>

      <label class="block w-44">
        <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</span>
        <PlatformaSelect
          v-model="filters.status"
          :options="statusOptions"
        />
      </label>

      <label class="block w-52">
        <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Provider</span>
        <PlatformaSelect
          v-model="filters.provider"
          :options="providerOptions"
        />
      </label>

      <button
        type="button"
        class="inline-flex h-11 items-center rounded-md bg-zinc-900 px-4 text-sm font-semibold text-white transition hover:bg-zinc-800"
        @click="refreshFromFirstPage"
      >
        Apply
      </button>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      {{ errorMessage }}
    </div>

    <div class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div class="app-scroll overflow-x-auto">
        <table class="min-w-full divide-y divide-zinc-200">
          <thead class="bg-zinc-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Member
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Date
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Provider
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Status
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Amount
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Platforma Fee
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Net
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-100 bg-white">
            <tr
              v-for="payment in rows"
              :key="payment.id"
              class="cursor-pointer hover:bg-zinc-50/80"
              @click="openOrderModal(payment)"
            >
              <td class="px-4 py-3 text-sm text-zinc-900">
                <div class="flex items-center gap-3">
                  <div class="relative h-9 w-9 shrink-0">
                    <div
                      v-if="shouldShowMemberAvatarSkeleton(payment)"
                      class="absolute inset-0 animate-pulse rounded-full border border-zinc-200 bg-zinc-200/70"
                    />
                    <img
                      :src="resolveMemberAvatarSrc(payment)"
                      :alt="`${memberName(payment)} avatar`"
                      class="h-9 w-9 rounded-full border border-zinc-200 object-cover transition-opacity duration-150"
                      :class="shouldShowMemberAvatarSkeleton(payment) ? 'opacity-0' : 'opacity-100'"
                      @load="handleMemberAvatarLoad(payment)"
                      @error="handleMemberAvatarError(payment)"
                    >
                  </div>
                  <div
                    class="min-w-0"
                  >
                    <p class="truncate text-sm font-medium text-zinc-900">
                      {{ memberName(payment) }}
                    </p>
                    <p class="truncate text-xs text-zinc-500">
                      {{ payment.memberEmail || '-' }}
                    </p>
                    <p class="truncate text-[11px] text-zinc-400">
                      Order: {{ payment.sourceOrderSlug || '-' }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-zinc-700">
                {{ formatDate(payment.createdAt) }}
              </td>
              <td class="px-4 py-3 text-sm text-zinc-700">
                {{ providerLabel(payment.provider) }}
              </td>
              <td class="px-4 py-3 text-sm text-zinc-700">
                <span class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize" :class="statusClass(payment.status)">
                  {{ payment.status }}
                </span>
              </td>
              <td class="px-4 py-3 text-right text-sm text-zinc-900">
                {{ money(payment.amountCents) }}
              </td>
              <td class="px-4 py-3 text-right text-sm text-zinc-700">
                {{ money(payment.platformFeeCents) }}
              </td>
              <td class="px-4 py-3 text-right text-sm text-zinc-900">
                {{ money(payment.netPayoutCents) }}
              </td>
            </tr>
            <tr v-if="!rows.length && !isLoading">
              <td
                colspan="7"
                class="px-4 py-14 text-center"
              >
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#2D8CFF]">
                  <ReceiptPercentIcon class="h-6 w-6" />
                </div>
                <p class="mt-3 text-sm font-semibold text-zinc-900">No payments found</p>
                <p class="mt-1 text-sm text-zinc-500">New member payments will appear here automatically.</p>
              </td>
            </tr>
            <tr v-if="isLoading">
              <td
                colspan="7"
                class="px-4 py-10 text-center text-sm text-zinc-500"
              >
                Loading payments...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex items-center justify-between">
      <p class="text-sm text-zinc-600">
        Page {{ pagination.page }} of {{ pagination.totalPages }} ({{ pagination.total }} payments)
      </p>
      <div class="flex gap-2">
        <button
          type="button"
          class="inline-flex h-9 items-center rounded-md border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-800 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="pagination.page <= 1 || isLoading"
          @click="goToPage(pagination.page - 1)"
        >
          Previous
        </button>
        <button
          type="button"
          class="inline-flex h-9 items-center rounded-md border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-800 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="pagination.page >= pagination.totalPages || isLoading"
          @click="goToPage(pagination.page + 1)"
        >
          Next
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="orderModalOpen"
        class="fixed inset-0 z-[2500] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        :aria-label="orderModalTitle"
        @click.self="closeOrderModal"
      >
        <div class="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <div>
            <h3 class="text-lg font-semibold text-zinc-900">
              {{ orderModalTitle }}
            </h3>
            <p class="mt-1 text-sm text-zinc-500">
              {{ orderModalSubtitle }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
            @click="closeOrderModal"
          >
            Close
          </button>
        </div>

        <div class="app-scroll max-h-[72vh] overflow-y-auto px-5 py-5">
          <p
            v-if="orderModalLoading"
            class="text-sm text-zinc-500"
          >
            Loading order details...
          </p>
          <p
            v-else-if="orderModalError"
            class="text-sm text-red-600"
          >
            {{ orderModalError }}
          </p>
          <div v-else class="space-y-5">
            <div class="grid grid-cols-1 gap-3 rounded-xl border border-zinc-200 bg-zinc-50/70 p-4 text-sm sm:grid-cols-2">
              <div><span class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Member</span><span class="mt-1 block font-medium text-zinc-900">{{ memberName(orderModalPayment) }}</span><span class="text-xs text-zinc-500">{{ orderModalPayment?.memberEmail || '-' }}</span></div>
              <div><span class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Payment total</span><span class="mt-1 block font-semibold text-zinc-900">{{ money(orderModalPayment?.amountCents) }}</span><span class="text-xs text-zinc-500">Net {{ money(orderModalPayment?.netPayoutCents) }}</span></div>
              <div><span class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Provider</span><span class="mt-1 block text-zinc-900">{{ providerLabel(orderModalPayment?.provider) }}</span><span class="text-xs text-zinc-500">{{ orderModalPayment?.processor || '-' }}</span></div>
              <div><span class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</span><span class="mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize" :class="statusClass(orderModalPayment?.status)">{{ orderModalPayment?.status || '-' }}</span></div>
              <div><span class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Payment reference</span><span class="mt-1 block break-all font-mono text-xs text-zinc-700">{{ orderModalPayment?.paymentPublicId || '-' }}</span></div>
              <div><span class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Processed</span><span class="mt-1 block text-zinc-900">{{ formatDate(orderModalPayment?.paidAt || orderModalPayment?.createdAt) }}</span></div>
            </div>

            <div v-if="!isStorefrontOrderPayment" class="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
              <div class="flex gap-3">
                <CreditCardIcon class="mt-0.5 h-5 w-5 shrink-0 text-[#2D8CFF]" />
                <div>
                  <p class="text-sm font-semibold text-blue-950">{{ paymentTypeLabel }}</p>
                  <p class="mt-1 text-sm text-blue-900/70">This payment is not tied to a storefront order, so its payment record is the confirmation.</p>
                </div>
              </div>
            </div>

            <template v-if="orderModalOrder">
            <p class="text-xs text-zinc-500">
              Checkout ref: {{ orderModalPayment?.sourceOrderSlug || '-' }}
            </p>
            <div class="grid grid-cols-1 gap-3 text-sm text-zinc-700 md:grid-cols-2">
              <div><span class="font-semibold text-zinc-900">Order #:</span> {{ orderModalOrder.orderNumber || '-' }}</div>
              <div><span class="font-semibold text-zinc-900">Confirmation #:</span> {{ orderModalOrder.confirmationNumber || '-' }}</div>
              <div><span class="font-semibold text-zinc-900">Customer:</span> {{ orderModalOrder.customerName || orderModalOrder.customerEmail || '-' }}</div>
              <div><span class="font-semibold text-zinc-900">Payment:</span> {{ orderModalOrder.paymentStatus || '-' }}</div>
              <div><span class="font-semibold text-zinc-900">Total:</span> {{ money(orderModalOrder.totalCents) }}</div>
              <div><span class="font-semibold text-zinc-900">Created:</span> {{ formatDate(orderModalOrder.createdAt) }}</div>
            </div>

            <div class="rounded-lg border border-zinc-200">
              <div class="border-b border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-900">
                Items
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-zinc-200">
                  <thead class="bg-zinc-50">
                    <tr>
                      <th class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Product
                      </th>
                      <th class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Variant
                      </th>
                      <th class="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Qty
                      </th>
                      <th class="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-zinc-100 bg-white">
                    <tr
                      v-for="item in orderModalItems"
                      :key="item.id"
                    >
                      <td class="px-4 py-2 text-sm text-zinc-900">
                        {{ item.productTitle || '-' }}
                      </td>
                      <td class="px-4 py-2 text-sm text-zinc-700">
                        {{ item.variantTitle || '-' }}
                      </td>
                      <td class="px-4 py-2 text-right text-sm text-zinc-700">
                        {{ item.quantity || 0 }}
                      </td>
                      <td class="px-4 py-2 text-right text-sm text-zinc-900">
                        {{ money(item.lineTotalCents) }}
                      </td>
                    </tr>
                    <tr v-if="!orderModalItems.length">
                      <td
                        colspan="4"
                        class="px-4 py-6 text-center text-sm text-zinc-500"
                      >
                        No order items found.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            </template>
          </div>
        </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { CreditCardIcon, ReceiptPercentIcon } from '@heroicons/vue/24/outline';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const storefrontApiProxyUrl = `${apiProxyUrl}/api/creator-storefront`;

const rows = ref([]);
const paymentAvatarStateByKey = ref({});
const isLoading = ref(false);
const errorMessage = ref('');
const orderModalOpen = ref(false);
const orderModalLoading = ref(false);
const orderModalError = ref('');
const orderModalPayment = ref(null);
const orderModalOrder = ref(null);
const orderModalItems = ref([]);
const filters = reactive({
  q: '',
  status: '',
  provider: '',
});
const pagination = reactive({
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 1,
});

const statusOptions = Object.freeze([
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'authorized', label: 'Authorized' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'canceled', label: 'Canceled' },
]);
const providerOptions = Object.freeze([
  { value: '', label: 'All providers' },
  { value: 'platforma_payments', label: 'Platforma Payments' },
  { value: 'creator_stripe', label: 'Creator Stripe' },
  { value: 'bitcoin', label: 'Bitcoin' },
]);
const defaultAvatarSrc = new URL('@/assets/media/images/default-avatar.png', import.meta.url).href;
const DEFAULT_MEMBER_PROFILE_PLACEHOLDER = 'src/assets/media/images/default-avatar.png';

const money = (valueCents) => {
  const cents = Number(valueCents || 0);
  return `$${(cents / 100).toFixed(2)}`;
};

const providerLabel = (value) => ({
  platforma_payments: 'Platforma Payments',
  creator_stripe: 'Creator Stripe',
  bitcoin: 'Bitcoin',
}[String(value || '').toLowerCase()] || String(value || '-').replaceAll('_', ' '));

const statusClass = (value) => {
  const status = String(value || '').toLowerCase();
  if (status === 'succeeded' || status === 'authorized') return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200';
  if (status === 'failed' || status === 'canceled') return 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200';
  if (status === 'refunded') return 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200';
  return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200';
};

const isStorefrontOrderPayment = computed(() => {
  const payment = orderModalPayment.value;
  const slug = String(payment?.sourceOrderSlug || '').trim().toLowerCase();
  return Boolean(Number(payment?.featureInstanceId || 0) && slug && !slug.startsWith('member-subscription-'));
});
const paymentTypeLabel = computed(() => {
  const slug = String(orderModalPayment.value?.sourceOrderSlug || '').toLowerCase();
  if (slug.startsWith('member-subscription-')) return 'Membership subscription payment';
  return 'Payment confirmation';
});
const orderModalTitle = computed(() => isStorefrontOrderPayment.value ? 'Order confirmation' : 'Payment details');
const orderModalSubtitle = computed(() => isStorefrontOrderPayment.value
  ? 'Review the storefront order tied to this payment.'
  : 'Review the member charge and settlement details.');

const memberName = (payment) => {
  const explicitName = String(payment?.memberName || '').trim();
  if (explicitName) return explicitName;
  const memberUserId = Number(payment?.memberUserId || 0);
  if (memberUserId > 0) return `Member #${memberUserId}`;
  return 'Member';
};

const toPaymentAvatarStateKey = (payment) => {
  const paymentId = Number(payment?.id || 0);
  if (paymentId > 0) return `payment:${paymentId}`;
  const memberUserId = Number(payment?.memberUserId || 0);
  if (memberUserId > 0) return `member:${memberUserId}`;
  const email = String(payment?.memberEmail || '').trim().toLowerCase();
  if (email) return `email:${email}`;
  return `fallback:${memberName(payment)}`;
};

const isKnownDefaultAvatarPlaceholder = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized === DEFAULT_MEMBER_PROFILE_PLACEHOLDER ||
    normalized === '/src/assets/media/images/default-avatar.png' ||
    normalized === 'default-avatar.png' ||
    normalized.endsWith('/default-avatar.png') ||
    normalized.includes('/media/images/default-avatar.png')
  );
};

const normalizeMemberAvatarCandidate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const lowered = raw.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') return '';
  if (isKnownDefaultAvatarPlaceholder(raw)) return '';
  return raw;
};

const getPaymentAvatarStateKey = (payment) => {
  return toPaymentAvatarStateKey(payment);
};

const readPaymentAvatarState = (payment) => {
  const key = getPaymentAvatarStateKey(payment);
  return paymentAvatarStateByKey.value[key] || null;
};

const writePaymentAvatarState = (payment, patch = {}) => {
  const key = getPaymentAvatarStateKey(payment);
  const previous = paymentAvatarStateByKey.value[key] || { loaded: false, failed: false };
  paymentAvatarStateByKey.value = {
    ...paymentAvatarStateByKey.value,
    [key]: {
      ...previous,
      ...patch,
    },
  };
};

const resolveMemberAvatarSrc = (payment) => {
  const customAvatar = normalizeMemberAvatarCandidate(payment?.memberAvatarUrl);
  if (!customAvatar) return defaultAvatarSrc;
  const state = readPaymentAvatarState(payment) || { loaded: false, failed: false };
  return state.failed ? defaultAvatarSrc : customAvatar;
};

const shouldShowMemberAvatarSkeleton = (payment) => {
  const customAvatar = normalizeMemberAvatarCandidate(payment?.memberAvatarUrl);
  if (!customAvatar) return false;
  const state = readPaymentAvatarState(payment) || { loaded: false, failed: false };
  return !state.loaded && !state.failed;
};

const handleMemberAvatarLoad = (payment) => {
  writePaymentAvatarState(payment, { loaded: true });
};

const handleMemberAvatarError = (payment) => {
  const customAvatar = normalizeMemberAvatarCandidate(payment?.memberAvatarUrl);
  if (!customAvatar) {
    writePaymentAvatarState(payment, { loaded: true });
    return;
  }
  const state = readPaymentAvatarState(payment) || { loaded: false, failed: false };
  if (!state.failed) {
    writePaymentAvatarState(payment, { failed: true, loaded: false });
  }
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const closeOrderModal = () => {
  orderModalOpen.value = false;
  orderModalLoading.value = false;
  orderModalError.value = '';
  orderModalPayment.value = null;
  orderModalOrder.value = null;
  orderModalItems.value = [];
};

const openOrderModal = async (payment) => {
  orderModalOpen.value = true;
  orderModalLoading.value = true;
  orderModalError.value = '';
  orderModalPayment.value = payment || null;
  orderModalOrder.value = null;
  orderModalItems.value = [];

  if (!isStorefrontOrderPayment.value) {
    orderModalLoading.value = false;
    return;
  }

  try {
    const featureInstanceId = Number(payment?.featureInstanceId || 0);
    const sourceOrderSlug = String(payment?.sourceOrderSlug || '').trim();
    const searchParams = new URLSearchParams({
      featureInstanceId: String(featureInstanceId),
      page: '1',
      pageSize: '5',
      search: sourceOrderSlug,
    });
    const orderListResponse = await fetch(
      `${storefrontApiProxyUrl}/platforma/orders?${searchParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    const orderListPayload = await orderListResponse.json().catch(() => ({}));
    if (!orderListResponse.ok || orderListPayload?.success === false) {
      throw new Error(String(orderListPayload?.message || 'Failed to load order list'));
    }

    const orders = Array.isArray(orderListPayload?.orders) ? orderListPayload.orders : [];
    const matchedOrder = orders.find((order) => String(order?.checkoutSessionTag || '') === sourceOrderSlug) || orders[0] || null;
    if (!matchedOrder?.id) {
      throw new Error('Order record not found for this payment.');
    }

    const detailParams = new URLSearchParams({
      featureInstanceId: String(featureInstanceId),
    });
    const detailResponse = await fetch(
      `${storefrontApiProxyUrl}/platforma/orders/${encodeURIComponent(matchedOrder.id)}?${detailParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    const detailPayload = await detailResponse.json().catch(() => ({}));
    if (!detailResponse.ok || detailPayload?.success === false) {
      throw new Error(String(detailPayload?.message || 'Failed to load order details'));
    }

    orderModalOrder.value = detailPayload?.order || null;
    orderModalItems.value = Array.isArray(detailPayload?.items) ? detailPayload.items : [];
  } catch (error) {
    orderModalError.value = String(error?.message || 'Failed to load order details');
  } finally {
    orderModalLoading.value = false;
  }
};

const fetchPayments = async (page = 1) => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pagination.pageSize),
    });
    if (filters.q) params.set('q', filters.q);
    if (filters.status) params.set('status', filters.status);
    if (filters.provider) params.set('provider', filters.provider);

    const response = await fetch(`${apiProxyUrl}/api/creator-payments/creator/payments?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(String(payload.message || 'Failed to load payments'));
    }

    const nextRows = Array.isArray(payload.payments) ? payload.payments : [];
    rows.value = nextRows;
    const preservedAvatarState = {};
    for (const row of nextRows) {
      const key = toPaymentAvatarStateKey(row);
      if (paymentAvatarStateByKey.value[key]) {
        preservedAvatarState[key] = paymentAvatarStateByKey.value[key];
      }
    }
    paymentAvatarStateByKey.value = preservedAvatarState;
    pagination.page = Number(payload.pagination?.page || page);
    pagination.pageSize = Number(payload.pagination?.pageSize || 25);
    pagination.total = Number(payload.pagination?.total || 0);
    pagination.totalPages = Number(payload.pagination?.totalPages || 1);
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to load payments');
    rows.value = [];
    pagination.total = 0;
    pagination.totalPages = 1;
  } finally {
    isLoading.value = false;
  }
};

const goToPage = (page) => {
  if (page < 1) return;
  fetchPayments(page);
};

const refreshFromFirstPage = () => {
  fetchPayments(1);
};

onMounted(() => {
  fetchPayments(1);
});
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(113, 113, 122, 0.38) transparent;
}
.app-scroll::-webkit-scrollbar { width: 7px; height: 7px; }
.app-scroll::-webkit-scrollbar-track { background: transparent; }
.app-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(113, 113, 122, 0.34); }
.app-scroll::-webkit-scrollbar-thumb:hover { background: rgba(82, 82, 91, 0.5); }
</style>
